import { Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { ThemeWrapper } from "@verity/components";
import { useContext, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import apiClient from "../../../../../helpers/api-client";
import ProctoringResultView from "../../../../../results/components/student-selection/videoplayer/ProctoringResultView";
import { PortalControllerContext } from "../../../../context";
import { CaretLeftIcon } from "../assets";
import AttendedPageSkeleton from "./AttendedPageSkeleton";
import InterviewUnattendedPage from "./InterviewUnattendedPage";
import UnattendedPageSkeleton from "./UnattendedPageSkeleton";
import styles from "./InterviewResultsPage.module.css";
import { AssessmentType, ProctoringOptionsType, SessionInfo } from "../types";
import i18n from "../../../../../i18n";

type LocationState = {
	from?: string;
};

export default function InterviewResultsPage() {
	const { portalContext } = useContext(PortalControllerContext);
	const { lmsProvidedCourseId } = portalContext;
	const { id } = useParams();
	const navigate = useNavigate();
	const location = useLocation() as { state: LocationState };
	const { from } = location.state || {};

	const [session, setSession] = useState<SessionInfo>();

	const {
		data: assessment,
		error: assessmentError,
		isLoading: assessmentLoading,
	} = useQuery({
		queryKey: ["assessment", id],
		enabled: !!id,
		queryFn: async (): Promise<AssessmentType> => {
			const result = await apiClient.assessment.getAssessmentByAssessmentId(Number(id));

			if (!result) {
				throw new Error("Assessment not found");
			}

			return result;
		},
	});

	const {
		data: proctoringConfigurationOptions,
		error: configError,
		isLoading: configLoading,
	} = useQuery({
		queryKey: ["proctoring-config", assessment?.quizId, lmsProvidedCourseId],
		enabled: !!assessment?.quizId && !!lmsProvidedCourseId,
		queryFn: async (): Promise<ProctoringOptionsType> => {
			const result = await apiClient.proctoringConfiguration.get({
				quizId: assessment!.quizId,
				courseId: lmsProvidedCourseId!,
			});

			//Ensure exists
			if (!result) {
				throw new Error("Missing proctoring configuration");
			}

			//Ensure candidateInterviewOptions exists
			if (!result.candidateInterviewOptions) {
				throw new Error("Missing candidate options");
			}

			//Do this to avoid type script error
			const { candidateInterviewOptions, ...rest } = result;
			return {
				...rest,
				candidateInterviewOptions,
			};
		},
	});

	const {
		data: sessions,
		error: sessionError,
		isLoading: sessionLoading,
	} = useQuery<SessionInfo[]>({
		queryKey: ["live-session", assessment?.quizId, assessment?.courseId],
		enabled: !!assessment?.quizId && !!assessment?.courseId,
		queryFn: async () => {
			const quizId = assessment?.quizId;
			const courseId = assessment?.courseId;
			const results = await apiClient.proctoringSession.getSessionResultList(quizId!, courseId!);

			setSession(results?.[0]);

			return results;
		},
	});

	if (assessmentLoading || configLoading || sessionLoading)
		return session ? <AttendedPageSkeleton /> : <UnattendedPageSkeleton />;

	if (assessmentError || configError || sessionError) {
		throw new Error(assessmentError?.message || configError?.message || sessionError?.message);
	}

	if (!assessment || !proctoringConfigurationOptions || !sessions || !portalContext.instituteId) return null;

	return (
		<ThemeWrapper scoped={false}>
			<div className={styles.pageWrapper}>
				{!session ? (
					<>
						<div className={styles.back} onClick={() => navigate(from || "/results")}>
							<CaretLeftIcon />
							<Typography>{i18n.t("candidate.resultsPage.button.back")}</Typography>
						</div>
						<InterviewUnattendedPage
							assessmentId={assessment.id}
							candidateOptions={proctoringConfigurationOptions.candidateInterviewOptions}
						/>
					</>
				) : (
					<ProctoringResultView
						instituteId={portalContext.instituteId}
						courseId={assessment.courseId}
						showVideo
						proctoringSessions={sessions}
						liveSessions={{}}
						assessmentInfo={{
							id: assessment.id,
							lmsQuizId: assessment.quizId,
							name: typeof assessment.quizInfo === "string" ? undefined : assessment.quizInfo?.name,
						}}
						selectedStudent={session?.studentId}
						studentList={sessions.map((s) => ({
							id: s.studentId,
							name: s.userInfo?.full_name || "",
							severityLevel: s.flagLevel || undefined,
						}))}
						setSelectedStudent={() => {}}
						setShowStudentResults={() => navigate(from || "/results")}
					/>
				)}
			</div>
		</ThemeWrapper>
	);
}
